/**
 * The Business Units screen (f5.1) -- the second hierarchy level.
 *
 * This replaces f1.1's phase placeholder behind the `business-units`
 * navigation key, which f1.3 already granted to every role and f1.1 already
 * labelled *"Delivered by p5"*. **f1.3's role-to-navigation mapping is not
 * edited**, and f1.1's shared placeholder component is left alone for the keys
 * still using it.
 *
 * **What the prototype draws that is deliberately absent**, so a reviewer can
 * check the list rather than file six defects:
 *
 * | Prototype element | Why it is not here | Owner |
 * | --- | --- | --- |
 * | `Clients` count | **Delivered by f6.1**, on the terms this feature set: counted in ONE query over every unit, with no parameter added to either route. The cell links through to the Clients screen filtered to this unit, client-side | -- delivered |
 * | `Input sheet` column | **Fabricated for six of nine.** The real tabs are `Input Sheet_Banking&Insurance`, `_DB`, `_Citi`, `_Industry`, `_New Business`, `_Others` -- not `_US BI`, `_US DB`, `_US CITI`, `_US IND`, `_US NB`, `_US OTH`. Only `_US 01`, `_US 02` and `_US 04` are right. The value is neither stored nor derivable from `code`, and the alias table is the mechanism that replaces it | -- not planned |
 * | `Export` | No requirement names an export on this screen; f13.1's is per cycle | -- not planned |
 * | A `Currency` column | Dropped from the table, not from the model: the reporting currency is still on the detail screen, still in the create dialog, and still summarised in the subtitle and the toolbar filter | -- |
 * | A `DM` column | **DM is a CLIENT role**, not a unit one. The brief's roles matrix, f4.2's spec and the server's role-to-level map all bind `DM` to `CLIENT`; a DM column here could only ever be empty | f6.1 |
 * | The nine seeded units | adr-16 forbids seeding hierarchy; the table ships empty | f8.2, f8.3 |
 * | The prototype's CM values | Wrong for five of the nine units, and it renders *"CM not assigned"* on `CU US New Business`, which has one. The BADGE is kept; the values are illustrative | f8.3 |
 *
 * **Every filter runs client-side.** The search box and the four selects --
 * group, currency, CM, status -- all work over the collection the server
 * returned whole, because it is nine rows and the endpoint takes no parameter
 * of any kind.
 *
 * Every value on screen is bound to the server's answer. Nothing here derives
 * a status label, a currency name, a permission or a visibility rule in the
 * browser.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  Ban,
  Network,
  Pencil,
  Plus,
  RotateCcw,
  RotateCw,
  Search,
  ShieldAlert,
} from 'lucide-react';

import type { BusinessUnit } from '@/api/client';
import {
  useBusinessUnitGroups,
  useBusinessUnits,
  useReactivateBusinessUnit,
} from '@/api/queries';
import { useSessionScope } from '@/session/SessionScopeContext';
import { CREATE_CHILD, EDIT_NODE, MANAGE_HIERARCHY, rowCan } from '@/session/permissions';
import { EmptyCell } from '@/ui/EmptyCell';
import { ScopedEmptyState } from '@/ui/ScopedEmptyState';
import { GroupParentLink } from '@/ui/ScopedParentLink';
import { SortableHeader } from '@/ui/SortableHeader';
import { nextSort, type SortState } from '@/ui/tableSort';
import { ownerFor, ownerOptions } from '@/routes/business-unit-groups/ownershipView';

import { DeactivateUnitDialog } from './DeactivateUnitDialog';
import { UnitDialog } from './UnitDialog';
import {
  DEFAULT_SORT,
  NO_FILTERS,
  badgeClassFor,
  currencyOptions,
  currencySummary,
  groupOptions,
  initialsOf,
  isActive,
  isRefusal,
  statusOptions,
  visibleUnits,
  type SortColumn,
  type UnitFilters,
} from './unitView';

/** The Business Unit Groups screen, for the "create a parent first" state. */
const GROUPS_PATH = '/business-unit-groups';

/** The status code an ACTIVE parent carries. The label is still the server's. */
const STATUS_ACTIVE = 'ACTIVE';

function PageHead({
  subtitle,
  action,
}: {
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <div className="page-head">
      <div>
        <h1 className="page-title">Business Units</h1>
        <div className="page-sub">
          {subtitle ?? 'Second level of the hierarchy, and the unit of COM data entry'}
        </div>
      </div>
      <div className="page-actions">{action}</div>
    </div>
  );
}

function StateCard({
  icon,
  warning = false,
  title,
  children,
}: {
  icon: ReactNode;
  warning?: boolean;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="card">
      <div className="card-body">
        <div className="empty">
          <div className={`e-icon${warning ? ' e-icon-warning' : ''}`}>{icon}</div>
          <h3>{title}</h3>
          {children}
        </div>
      </div>
    </div>
  );
}

function NoAccess() {
  return (
    <>
      <PageHead />
      <StateCard warning icon={<ShieldAlert aria-hidden="true" />} title="No access">
        <p>
          The server refuses this area for your account, and records the refusal in the
          audit log.
        </p>
      </StateCard>
    </>
  );
}

/** Bars, not an empty table: distinguishable from a list with no rows. */
function LoadingSkeleton() {
  return (
    <>
      <PageHead />
      <div className="card" aria-busy="true" aria-label="Loading Business Units">
        <div className="card-body">
          {[0, 1, 2, 3, 4].map((index) => (
            <span key={index} className="skeleton-bar group-skeleton-line" />
          ))}
        </div>
      </div>
    </>
  );
}

/**
 * f9.2: the route is guarded by `RequirePermittedNavigation` in `App.tsx`, so
 * this screen no longer repeats the navigation check. Presentation only, as
 * ever: every endpoint below authorizes on its own authority.
 */
export function BusinessUnitsPage() {
  const units = useBusinessUnits();
  // The parents a create can name. Read here rather than inside the dialog so
  // the `New Business Unit` control can be disabled -- with a reason -- when
  // there is no active group to be the required first field's value.
  const groups = useBusinessUnitGroups();
  const { can } = useSessionScope();
  // SESSION-level: "may this caller write anything" decides whether the
  // list-level control is rendered. Each row's controls come from its own
  // `permissions`, and the create dialog's parents from theirs.
  const mayManage = can(MANAGE_HIERARCHY);

  // The Business Unit Groups screen links through with `?group=<id>`. It is a
  // CLIENT-side filter seeded from the URL, not a server parameter: the
  // collection endpoint takes none, and f4.1's assertion that no hierarchy
  // operation accepts a filter still holds verbatim.
  const [params] = useSearchParams();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<UnitFilters>(() => ({
    ...NO_FILTERS,
    groupId: params.get('group') ?? '',
  }));
  const [sort, setSort] = useState<SortState<SortColumn>>(DEFAULT_SORT);
  const [editing, setEditing] = useState<BusinessUnit | null>(null);
  const [creating, setCreating] = useState(false);
  const [deactivating, setDeactivating] = useState<BusinessUnit | null>(null);

  const all = units.data?.business_units ?? [];
  const visible = useMemo(
    () => visibleUnits(all, { search, filters, sort }),
    [all, search, filters, sort],
  );

  const activeGroups = useMemo(
    () =>
      (groups.data?.business_unit_groups ?? []).filter(
        (group) => group.status.code === STATUS_ACTIVE,
      ),
    [groups.data],
  );
  // The parents a create may name: active, AND carrying the server's
  // `hierarchy:create_child` for this caller. Never re-derived here.
  const parentGroups = useMemo(
    () => activeGroups.filter((group) => rowCan(group, CREATE_CHILD)),
    [activeGroups],
  );

  if (units.isPending || groups.isPending) return <LoadingSkeleton />;

  const failure = units.error ?? groups.error;
  if (failure) {
    // A 403 is a refusal, not a failure -- and it never triggers the
    // interactive re-authentication a 401 does (f1.2).
    if (isRefusal(failure)) return <NoAccess />;
    return (
      <>
        <PageHead />
        <StateCard
          warning
          icon={<AlertTriangle aria-hidden="true" />}
          title="The Business Units could not be loaded"
        >
          <p>{failure instanceof Error ? failure.message : 'Unexpected error.'}</p>
          <p className="empty-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                void units.refetch();
                void groups.refetch();
              }}
            >
              <RotateCw aria-hidden="true" /> Retry
            </button>
          </p>
        </StateCard>
      </>
    );
  }

  const toggleSort = (column: SortColumn) => setSort((current) => nextSort(current, column));
  const setFilter = (field: keyof UnitFilters, value: string) =>
    setFilters((current) => ({ ...current, [field]: value }));

  // Opening a dialog whose required first field has no options is a dead end,
  // so the control is disabled and SAYS WHY rather than being hidden.
  const canCreate = mayManage && parentGroups.length > 0;
  const createTitle = !mayManage || canCreate
    ? undefined
    : activeGroups.length === 0
      ? 'Create an active Business Unit Group first: every unit must have one.'
      : 'None of the Business Unit Groups you can see allows you to create a Business Unit under it.';

  return (
    <>
      <PageHead
        subtitle={
          // The prototype's subtitle, computed rather than typed: it reads
          // "9 units · 8 reporting USD, 1 reporting EUR" only when the data
          // says so.
          all.length === 0
            ? 'Second level of the hierarchy, and the unit of COM data entry'
            : `${all.length} ${all.length === 1 ? 'unit' : 'units'} · ${currencySummary(all)}`
        }
        action={
          mayManage ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={!canCreate}
              title={createTitle}
              onClick={() => setCreating(true)}
            >
              <Plus aria-hidden="true" /> New Business Unit
            </button>
          ) : undefined
        }
      />

      <div className="toolbar">
        <div className="search-field">
          <Search aria-hidden="true" />
          <input
            type="search"
            value={search}
            placeholder="Search unit, code or Kimble alias"
            aria-label="Search unit, code or Kimble alias"
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        {/* Options are the values actually present, not every value the server
            knows: a filter offering a currency no row carries is a worse
            control than one offering the ones that do (f4.2's rule). */}
        <select
          className="toolbar-select"
          aria-label="Filter by Business Unit Group"
          value={filters.groupId}
          onChange={(event) => setFilter('groupId', event.target.value)}
        >
          <option value="">All Business Unit Groups</option>
          {groupOptions(all).map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
        <select
          className="toolbar-select"
          aria-label="Filter by reporting currency"
          value={filters.currencyCode}
          onChange={(event) => setFilter('currencyCode', event.target.value)}
        >
          <option value="">All currencies</option>
          {currencyOptions(all).map((option) => (
            <option key={option.code} value={option.code}>
              {option.code}
            </option>
          ))}
        </select>
        <select
          className="toolbar-select"
          aria-label="Filter by CM"
          value={filters.cmUserId}
          onChange={(event) => setFilter('cmUserId', event.target.value)}
        >
          <option value="">All CMs</option>
          {ownerOptions(all, 'CM').map((option) => (
            <option key={option.userId} value={option.userId}>
              {option.displayName}
            </option>
          ))}
        </select>
        <select
          className="toolbar-select"
          aria-label="Filter by status"
          value={filters.statusCode}
          onChange={(event) => setFilter('statusCode', event.target.value)}
        >
          <option value="">All statuses</option>
          {statusOptions(all).map((option) => (
            <option key={option.code} value={option.code}>
              {option.name}
            </option>
          ))}
        </select>
      </div>

      <div className="card">
        <div className="table-scroll">
          <table className="data zebra">
            <thead>
              <tr>
                <SortableHeader
                  column="group"
                  label="Business Unit Group"
                  sort={sort}
                  onSort={toggleSort}
                />
                <SortableHeader column="code" label="Code" sort={sort} onSort={toggleSort} />
                <SortableHeader
                  column="name"
                  label="Business Unit"
                  sort={sort}
                  onSort={toggleSort}
                />
                <SortableHeader column="CM" label="CM" sort={sort} onSort={toggleSort} />
                <SortableHeader column="DM" label="DM" sort={sort} onSort={toggleSort} />
                {/* f6.1's delivered count, and the drill-through it enables. */}
                <SortableHeader
                  column="clients"
                  label="Clients"
                  sort={sort}
                  onSort={toggleSort}
                  className="num"
                />
                <SortableHeader
                  column="status"
                  label="Status"
                  sort={sort}
                  onSort={toggleSort}
                />
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <ScopedEmptyState
                      searching={all.length > 0}
                      matchNoun="Business Units"
                      collectionNoun="Business Units"
                      icon={<Network aria-hidden="true" />}
                      freshInstallHint="Create the first unit, or wait for the master workbook import to bring the nine across."
                      parentMissing={
                        activeGroups.length > 0
                          ? null
                          : {
                              title: 'No Business Unit Group exists yet',
                              hint: (
                                <>
                                  Every Business Unit hangs off a group, so a group has to exist
                                  first.{' '}
                                  <Link className="cell-link" to={GROUPS_PATH}>
                                    Create one on the Business Unit Groups screen
                                  </Link>
                                  .
                                </>
                              ),
                            }
                      }
                    />
                  </td>
                </tr>
              ) : (
                visible.map((row) => (
                  <UnitRow
                    key={row.id}
                    unit={row}
                    onEdit={() => setEditing(row)}
                    onDeactivate={() => setDeactivating(row)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Not a disclosure: `all` is the collection the server ALREADY scoped,
          and `visible` is what the caller's own search and filters left of it.
          It reports the caller's narrowing, never the server's. */}
      <div className="pagination">
        <span className="info">
          {visible.length === all.length
            ? `Showing all ${all.length} Business ${all.length === 1 ? 'Unit' : 'Units'}`
            : `Showing ${visible.length} of ${all.length} Business Units`}
        </span>
      </div>

      {(creating || editing) && (
        <UnitDialog
          unit={editing}
          groups={parentGroups}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
      {deactivating && (
        <DeactivateUnitDialog
          unit={deactivating}
          onClose={() => setDeactivating(null)}
        />
      )}
    </>
  );
}

function UnitRow({
  unit,
  onEdit,
  onDeactivate,
}: {
  unit: BusinessUnit;
  onEdit: () => void;
  onDeactivate: () => void;
}) {
  const reactivate = useReactivateBusinessUnit();
  const busy = reactivate.isPending;
  const active = isActive(unit);
  // The ROW's own capability, projected by the server from f9.1's grant table.
  const mayEdit = rowCan(unit, EDIT_NODE);
  const cm = ownerFor(unit, 'CM');
  const dm = ownerFor(unit, 'DM');

  return (
    <tr>
      <td>
        <GroupParentLink group={unit.business_unit_group} />
      </td>
      <td className="mono">{unit.code}</td>
      <td>
        <Link className="cell-link" to={`/business-units/${unit.id}`}>
          {unit.name}
        </Link>
        {/* ALL the aliases, not one. The prototype draws a single chip; the
            estate holds nine orphan spellings across at least four units. */}
        {unit.aliases.length > 0 && (
          <span className="sub">
            {unit.aliases.length === 1 ? 'alias: ' : 'aliases: '}
            {unit.aliases.join(', ')}
          </span>
        )}
      </td>
      <td>
        {cm ? (
          <span className="row owner-cell">
            <span className="avatar sm" aria-hidden="true">
              {initialsOf(cm.display_name)}
            </span>
            {cm.display_name}
          </span>
        ) : (
          // The prototype's badge, kept. It reports the ABSENCE OF AN
          // ASSIGNMENT and nothing more -- never a client-side inference about
          // permissions. f4.2 renders an em dash for an unowned group; at this
          // level an unowned unit is a gap worth surfacing, and the prototype
          // says so.
          <span className="badge pill-warning">CM not assigned</span>
        )}
      </td>
      <td>
        {dm ? (
          <span className="row owner-cell">
            <span className="avatar sm" aria-hidden="true">
              {initialsOf(dm.display_name)}
            </span>
            {dm.display_name}
          </span>
        ) : (
          // The shared empty-cell mark, NOT the CM's warning badge. A unit
          // with no CM is a gap worth surfacing; a unit with no DM is
          // ordinary, because the delivery manager is commonly named on the
          // clients beneath it instead.
          <EmptyCell />
        )}
      </td>
      <td className="num">
        {/*
          f6.1's `client_count`. The link filters the Clients screen to this
          unit CLIENT-side -- `?unit=<id>` is read by that screen's own state,
          not sent to the server, so f4.1's "no operation accepts a filter
          parameter" assertion still holds.
        */}
        {unit.client_count === 0 ? (
          <EmptyCell />
        ) : (
          <Link className="cell-link" to={`/clients?unit=${unit.id}`}>
            {unit.client_count}
          </Link>
        )}
      </td>
      <td>
        <span className={badgeClassFor(unit.status.code)}>{unit.status.name}</span>
      </td>
      <td>
        <div className="row-actions">
          {mayEdit && (
            <>
              <button
                type="button"
                className="btn btn-secondary btn-icon"
                title="Edit"
                aria-label={`Edit ${unit.name}`}
                disabled={busy}
                onClick={onEdit}
              >
                <Pencil aria-hidden="true" />
              </button>
              {active ? (
                <button
                  type="button"
                  className="btn btn-secondary btn-icon"
                  title="Deactivate"
                  aria-label={`Deactivate ${unit.name}`}
                  disabled={busy}
                  onClick={onDeactivate}
                >
                  <Ban aria-hidden="true" />
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-secondary btn-icon"
                  title="Reactivate"
                  aria-label={`Reactivate ${unit.name}`}
                  disabled={busy}
                  // Unguarded and unconfirmed: reactivating a parent cannot
                  // orphan anything, and it grants nothing new.
                  onClick={() => reactivate.mutate(unit.id)}
                >
                  <RotateCcw aria-hidden="true" />
                </button>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
}
